/**
 * Server-safe route <head> builder.
 *
 * Produces exactly the tags the old client-side SEOHead wrote into
 * document.head, but as a TanStack Router `head()` payload so they are present
 * in the SERVER HTML for crawlers that never run JavaScript.
 *
 * Schema shapes come from src/lib/seo.ts unchanged — this module only
 * serialises them.
 */
import { COMPANY } from "@/lib/seo";

/** Sitewide entities owned by the root route — never re-emitted per page. */
const GLOBAL_IDS = new Set([`${COMPANY.url}/#organization`, `${COMPANY.url}/#localbusiness`]);

export interface RouteHeadInput {
  title: string;
  description: string;
  /** Site-root path, e.g. "/about". Omitted → site root; null → no canonical/og:url. */
  canonical?: string | null | undefined;
  ogImage?: string | undefined;
  noIndex?: boolean | undefined;
  follow?: boolean | undefined;
  schemas?: (object | null | undefined)[] | undefined;
}

/**
 * Rendered length of a title once HTML-escaped — `&` and `'` cost 5-6 chars in
 * the served markup, which is what a SERP truncation audit measures.
 */
export function renderedLength(value: string) {
  return value.replace(/&/g, "&amp;").replace(/'/g, "&#x27;").replace(/"/g, "&quot;").length;
}

/** Mirrors the SEOHead phone-suffix rule, measured on the escaped output. */
/** Titles that must render verbatim (no phone suffix). */
const EXACT_TITLES = new Set(["ASHRAE Energy Audit Service — Louisville KY"]);

export function titleWithPhone(title: string) {
  if (EXACT_TITLES.has(title)) return title;
  const suffix = ` | ${COMPANY.phone}`;
  return title.includes("502") || renderedLength(title + suffix) > 60 ? title : `${title}${suffix}`;
}


export function jsonLdScripts(schemas: (object | null | undefined)[]) {
  return schemas
    .filter((s): s is object => Boolean(s))
    .filter((s) => {
      const id = (s as Record<string, unknown>)["@id"];
      return !(typeof id === "string" && GLOBAL_IDS.has(id));
    })
    .map((schema) => ({
      type: "application/ld+json",
      children: JSON.stringify(schema),
    }));
}

export function buildHead({ title, description, canonical, ogImage, noIndex, follow, schemas = [] }: RouteHeadInput) {
  const fullUrl = canonical === null ? null : canonical ? `${COMPANY.url}${canonical}` : COMPANY.url;
  const image = ogImage || `${COMPANY.url}/images/og-image.jpg`;
  const finalTitle = titleWithPhone(title);

  return {
    meta: [
      { title: finalTitle },
      { name: "description", content: description },
      { name: "robots", content: noIndex ? (follow ? "noindex, follow" : "noindex, nofollow") : "index, follow, max-image-preview:large" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      ...(fullUrl ? [{ property: "og:url", content: fullUrl }] : []),
      { property: "og:image", content: image },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:site_name", content: COMPANY.name },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image },
    ],
    links: fullUrl ? [{ rel: "canonical", href: fullUrl }] : [],
    scripts: jsonLdScripts(schemas),
  };
}
